import { globalState } from '@codeleap/store'
import { ChatUser, ChatChannelInstance, ChatMessage, ThreadParent, ChatLocalMesage, FocusedMessageInfo, ChatSelectModes, SimpleChannel } from './types'
import { alert } from '@codeleap/modals'
import { Constants } from './constants'

const SELECTED_MESSAGES_FOWARDED = Constants.forwardedMessage.maxSelectedMessages
const MAX_FORWARD_CHATS = Constants.forwardedMessage.maxSelectedChannels

type ChatUserImageOverride = string | null

type Message = ChatLocalMesage

export type ChatStoreValue = {
  channel: ChatChannelInstance | null
  thread: ThreadParent
  userImageOverrides: Record<string, ChatUserImageOverride>
  focusedMessage?: FocusedMessageInfo | null
  editMode?: boolean
  selectMode?: ChatSelectModes
  selectedMessages?: Message[]
  messageToScroll?:  ChatLocalMesage
  toastMessage?: string
  isMessageActionMenuOpen?: boolean
  selectedChats?: SimpleChannel[]
  chatForceRender: ChatChannelInstance['id']
  selfLeaveChannel?: string | null
  unreadCount: number
  isRefreshingMembers: boolean
  lastOwnMessageReadDate?: Date | null
}

const initialState: ChatStoreValue = {
  channel: null,
  thread: null,
  userImageOverrides: {},
  editMode: false,
  selectMode: null,
  selectedMessages: [],
  messageToScroll: null,
  toastMessage: null,
  isMessageActionMenuOpen: false,
  selectedChats: [],
  chatForceRender: null,
  selfLeaveChannel: null,
  unreadCount: 0,
  isRefreshingMembers: false,
  lastOwnMessageReadDate: null,
}

export const state = globalState<ChatStoreValue>(initialState)

export const userState = globalState<ChatUser | null>(null, {
  persistKey: 'chatUser',
})

type StoredChatUser = ChatUser & {
  image?: string | null
}

export function clear() {
  state.set(initialState)
  userState.reset(null)
}

export function setLastOwnMessageRead(date: Date | null) {
  state.set({ lastOwnMessageReadDate: date })
}

export function setUnreadCount(count: number) {
  state.set({ unreadCount: count })
}

export function getUnreadCount() {
  return state.get().unreadCount
}

export function useUnreadCount() {
  return state.use(s => s?.unreadCount ?? 0)
}

export function getSelfLeaveChannel() {
  return state.get().selfLeaveChannel
}

export function setSelfLeaveChannel(channelId: string) {
  state.set({ selfLeaveChannel: channelId })
}

let timeout = null as NodeJS.Timeout | null

export function forceChatRerender(chatId?: string) {
  const now = Date.now()
  state.set({ chatForceRender: `${chatId}-${now}` })
}

export function shouldChatRerender(chatId: string) {
  const chatForceRender = state.get().chatForceRender
  return chatForceRender?.startsWith(chatId)
}

export function useShouldChatRerender(chatId: string) {
  return state.use(s => s?.chatForceRender?.startsWith(chatId))
}

export function setEditMode(editMode: boolean) {
  state.set({ editMode })
}

export function cancelEditMode() {
  state.set({ editMode: false })
}

export function getEditMode() {
  return state.get()?.editMode
}

export function useEditMode() {
  const editMode = state.use(s => s?.editMode)

  return { editMode }
}

export function setToastMessage(toastMessage: string, duration = 5000) {
  if (timeout) {
    clearTimeout(timeout)
  }

  state.set({ toastMessage })

  timeout = setTimeout(() => {
    state.set({ toastMessage: null })
  }, duration)
}

export function setMessageToScroll(messageToScroll: ChatLocalMesage) {
  state.set({ messageToScroll })
  setTimeout(() => {
    state.set({ messageToScroll: null })
  })
}

export function getUpdatedUserImage(userId?: string) {
  if (!userId) return undefined

  return state.get().userImageOverrides?.[userId]
}

export function setUpdatedUserImage(userId: string, image: ChatUserImageOverride) {
  if (!userId) return

  const currentImage = getUpdatedUserImage(userId)

  if (currentImage === image) return

  state.set({
    userImageOverrides: {
      ...state.get().userImageOverrides,
      [userId]: image,
    },
  })
}

export function setThread(thread: ThreadParent) {
  state.set({ thread })
}

export function useThread() {
  return state.use(s => s?.thread)
}

export function getThread() {
  return state.get()?.thread
}

export function getSelectMode(props?: {
  threadList?: boolean
  selectMode?: ChatSelectModes
  thread?: ThreadParent
}) {
  const { threadList = false, selectMode, thread } = props || {}

  return selectMode ?? state.get()?.selectMode ?? null
}

export function useSelectMode(props?: { threadList?: boolean }) {
  const { threadList = false } = props || {}
  const selectMode = state.use(s => s?.selectMode)
  const thread = state.use(s => s?.thread)
  const _selectMode = getSelectMode({ threadList, selectMode, thread })

  return { selectMode: _selectMode, selectModeActive: !!_selectMode }
}

export function useSelectedMessages() {
  return state.use(s => s?.selectedMessages)
}

export function useIsMessageSelected(messageId: string) {
  return !!state.use(s => s.selectedMessages?.find(msg => msg?.id === messageId))
}

export function cancelSelectMode() {
  state.set({ selectMode: null, selectedMessages: [] })
}

export function setSelectMode(mode: ChatSelectModes, initialSelectedMessages?: Message[]) {
  state.set({ selectMode: mode, selectedMessages: initialSelectedMessages || [] })
}

export function selectMessage(selectedMessage: Message) {
  const { selectedMessages = [], selectMode } = state.get()


  if (!!selectedMessages.find(msg => msg?.id === selectedMessage?.id)) {
    state.set({
      selectedMessages: selectedMessages.filter(msg => msg?.id !== selectedMessage?.id)
    })
  } else {
    if (selectedMessages.length >= SELECTED_MESSAGES_FOWARDED && selectMode === 'forward') {
      alert.info({
        title: 'Limit reached',
        body: `You can only forward up to ${SELECTED_MESSAGES_FOWARDED} messages at once.`,
      })
      return
    }

    state.set({
      selectedMessages: [...selectedMessages, selectedMessage]
    })
  }
}

export function useIsForwardLimitReached() {
  const isLimitReached = state.use(s => s.selectedChats?.length >= MAX_FORWARD_CHATS)

  return { isLimitReached }
}

export function selectChat(chat: SimpleChannel) {
  const { selectedChats = [] } = state.get()
  const isSelected = selectedChats.some(c => c.id === chat.id)
  if (isSelected) {
    state.set({
      selectedChats: selectedChats.filter(c => c.id !== chat.id)
    })
  } else {
    state.set({
      selectedChats: [...selectedChats, chat]
    })
  }
}

export function setSelectedChats(chats: SimpleChannel[]) {
  state.set({ selectedChats: chats })
}

export function setFocusedMessage(data: FocusedMessageInfo | null) {
  state.set({ focusedMessage: data })
}

export function useFocusedMessage(props?: {threadList?: boolean}) {
  const { threadList = false } = props || {}
  const focusedMessage = state.use(s => s?.focusedMessage)
  const thread = state.use(s => s?.thread)
  return !thread ? focusedMessage : threadList ? focusedMessage : null
}

export function useUpdatedUserImage(userId?: string) {
  return state.use(s => {
    if (!userId) return undefined

    return s?.userImageOverrides?.[userId]
  })
}

export function normalizeUser(user: ChatUser | null) {
  if (!user) return null

  const normalizedUser = {
    ...user,
  } as StoredChatUser

  if (!Object.prototype.hasOwnProperty.call(user, 'image')) {
    normalizedUser.image = null
  }

  return normalizedUser as ChatUser
}

export function setUser(user: ChatUser | null) {
  userState.reset(normalizeUser(user))
}

export function useUser() {
  return userState.use()
}

export function getUser() {
  return userState.get()
}

export function setChannel(channel: ChatChannelInstance) {
  state.set({ channel })
}

export function getChannel() {
  return state.get()?.channel
}

export function setIsMessageActionMenuOpen(isOpen: boolean) {
  state.set({ isMessageActionMenuOpen: isOpen })
}

export function useIsMessageActionMenuOpen() {
  return state.use(s => s?.isMessageActionMenuOpen)
}

export function setIsRefreshingMembers(value: boolean) {
  state.set({ isRefreshingMembers: value })
}

export function useIsRefreshingMembers() {
  return state.use(s => s?.isRefreshingMembers)
}
